/* V25_APPLICATION_UI_USERFLOW_V1 */
import React, { useEffect, useMemo, useState } from "react";
import { researchApi } from "../../../api/researchApi";
import ConfigurationCanvas from "./ConfigurationCanvas";
import TerrainConfigurationExperience from "./TerrainConfigurationExperience";
import InspectDrawer from "./InspectDrawer";
import ConfigurationCompare from "./ConfigurationCompare";
import V24ProfileCanvas from "./V24ProfileCanvas";
import {
  preferredState,
  sortStates,
  stabilityLabel,
} from "./needsApplicationModel";
import "./capabilityWorkspace.css";

export default function CapabilityWorkspace({
  capabilityId,
  capabilityLabel,
  fallback,
  onResearch,
}) {
  const [availability, setAvailability] = useState("loading");
  const [states, setStates] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  const [inspectGroup, setInspectGroup] = useState(null);
  const [inspectIndex, setInspectIndex] = useState(0);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [inspectDetail, setInspectDetail] = useState(null);
  const [rootDetail, setRootDetail] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [viewMode, setViewMode] = useState("terrain");

  useEffect(() => {
    let alive = true;

    setAvailability("loading");
    setStates([]);
    setSelectedStateId(null);
    setDetail(null);
    setError(null);
    setInspectGroup(null);
    setCompareOpen(false);

    researchApi
      .listStates({
        capabilityId,
        multiRootOnly: false,
        limit: 100,
        offset: 0,
      })
      .then((payload) => {
        if (!alive) return;
        const next = sortStates(Array.isArray(payload) ? payload : []);
        if (!next.length) {
          setAvailability("no-state");
          return;
        }
        setStates(next);
        setSelectedStateId(preferredState(next)?.id || next[0].id);
        setAvailability("ready");
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setAvailability("unavailable");
      });

    return () => {
      alive = false;
    };
  }, [capabilityId]);

  useEffect(() => {
    if (!selectedStateId || availability !== "ready") return;
    let alive = true;

    setDetail(null);
    setError(null);
    setInspectGroup(null);

    researchApi
      .getState(selectedStateId)
      .then((payload) => {
        if (alive) setDetail(payload);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setAvailability("unavailable");
      });

    return () => {
      alive = false;
    };
  }, [selectedStateId, availability]);

  const selectedSummary = useMemo(
    () => states.find((state) => state.id === selectedStateId) || null,
    [states, selectedStateId]
  );

  async function inspect(group, index = 0) {
    if (!group?.pathways?.length) return;

    const safeIndex = Math.max(0, Math.min(index, group.pathways.length - 1));
    const pathway = group.pathways[safeIndex];

    setInspectGroup(group);
    setInspectIndex(safeIndex);
    setInspectLoading(true);
    setInspectDetail(null);
    setRootDetail(null);

    try {
      const next = await researchApi.getPathway(pathway.id);
      setInspectDetail(next);

      const rootId =
        next?.pathway?.root_id ||
        next?.study_root?.id ||
        pathway?.root_id;

      if (rootId) {
        try {
          const root = await researchApi.getRoot(rootId, {
            sourceLimit: 5,
            atomLimit: 5,
          });
          setRootDetail(root);
        } catch (_) {
          setRootDetail(null);
        }
      }
    } catch (err) {
      setInspectDetail({ error: err?.message || String(err) });
    } finally {
      setInspectLoading(false);
    }
  }

  function closeInspect() {
    setInspectGroup(null);
    setInspectDetail(null);
    setRootDetail(null);
    setInspectIndex(0);
  }

  if (availability === "unavailable" || availability === "no-state") {
    return (
      <V24ProfileCanvas
        capabilityId={capabilityId}
        capabilityLabel={capabilityLabel}
        reason={availability}
        onResearch={onResearch}
      />
    );
  }

  if (availability === "loading" || !selectedSummary || !detail) {
    return (
      <section className="v25a-workspace v25a-loading">
        <span className="v25a-eyebrow">
          RESEARCH RUNTIME · CAPABILITY WORKSPACE
        </span>
        <h2>{capabilityLabel}</h2>
        <div className="v25a-loading-box">
          <i />
          <b>Laster konfigurasjonstopologi…</b>
          <small>Analytical receipt · V25.4 frozen</small>
        </div>
      </section>
    );
  }

  const comparisonCount = detail.comparative_deltas?.length || 0;

  return (
    <section className="v25a-workspace">
      <header className="v25a-workspace-head">
        <div>
          <span className="v25a-eyebrow">
            KONFIGURASJON / CONFIGURATION
          </span>
          <h1>{capabilityLabel}</h1>
          <p>
            Ett stabilt capability-sentrum. Bytt konfigurasjon uten å forlate
            arbeidsflaten; inspiser research i en drawer uten å miste kontekst.
          </p>
        </div>

        <div className="v25a-head-receipts">
          <span>ANALYTICAL RECEIPT · V25.4 FROZEN</span>
          <span>ROOT-NORMALIZED</span>
          {comparisonCount > 0 ? (
            <span className="future">
              {comparisonCount} comparative Δ · available
            </span>
          ) : null}
        </div>
      </header>

      <nav className="v25a-config-switcher" aria-label="Velg konfigurasjon">
        <span className="v25a-switcher-label">Konfigurasjon</span>
        <div>
          {states.map((state) => {
            const active = state.id === selectedStateId;
            return (
              <button
                key={state.id}
                type="button"
                className={active ? "active" : ""}
                onClick={() => setSelectedStateId(state.id)}
              >
                <b>{state.configuration_label || state.configuration_id}</b>
                <small>
                  {state.independent_root_count} roots ·{" "}
                  {stabilityLabel(state.stability_class)}
                </small>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="v25a-state-context">
        <span>
          <b>{selectedSummary.configuration_label}</b>
          {" × "}
          {capabilityLabel}
        </span>
        <small>
          configuration changes in place · zoom = analytical resolution
        </small>
      </div>

      {comparisonCount > 0 ? (
        <section className="rr-compare-entry">
          <div>
            <span className="v25a-eyebrow">COMPARATIVE CONFIGURATION</span>
            <b>{comparisonCount} warranted comparison{comparisonCount === 1 ? "" : "s"} available</b>
            <small>
              Compare topology around the same stable capability.
            </small>
          </div>
          <button type="button" onClick={() => setCompareOpen(true)}>
            Compare configurations →
          </button>
        </section>
      ) : null}

      <section className="rr-configuration-visual-mode">
        <div className="rr-view-mode-switcher">
          <div>
            <span className="v25a-eyebrow">CONFIGURATION VIEW</span>
            <b>{viewMode === "terrain" ? "Relational terrain" : "Analytical topology"}</b>
          </div>
          <div className="rr-view-mode-actions">
            <button type="button" className={viewMode === "terrain" ? "active" : ""} onClick={() => setViewMode("terrain")}>Terrain</button>
            <button type="button" className={viewMode === "topology" ? "active" : ""} onClick={() => setViewMode("topology")}>Analytical topology</button>
            {comparisonCount > 0 ? <button type="button" className="projection" onClick={() => setCompareOpen(true)}>Projection Lens / Compare →</button> : null}
          </div>
        </div>

        {viewMode === "terrain" ? (
          <TerrainConfigurationExperience
            capabilityLabel={capabilityLabel}
            state={detail}
            pathways={detail.pathways || []}
            comparisonCount={comparisonCount}
            onInspect={(group) => inspect(group, 0)}
            onCompare={() => setCompareOpen(true)}
          />
        ) : (
          <ConfigurationCanvas
        capabilityLabel={capabilityLabel}
        state={detail}
        pathways={detail.pathways || []}
        onInspect={(group) => inspect(group, 0)}
      />
        )}
      </section>

      <footer className="v25a-workspace-foot">
        <div>
          <b>Research remains fixed.</b>
          <span>
            Configuration changes what becomes relationally legible; the source
            substrate is not rewritten.
          </span>
        </div>
</footer>

      <ConfigurationCompare
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        capabilityId={capabilityId}
        capabilityLabel={capabilityLabel}
        currentStateId={selectedStateId}
        states={states}
        deltas={detail.comparative_deltas || []}
        onOpenState={(stateId) => {
          if (!stateId) return;
          setSelectedStateId(stateId);
          setCompareOpen(false);
        }}
      />

      <InspectDrawer
        stateId={selectedStateId}
        stateLabel={`${selectedSummary.configuration_label} × ${capabilityLabel}`}
        group={inspectGroup}
        index={inspectIndex}
        loading={inspectLoading}
        detail={inspectDetail}
        rootDetail={rootDetail}
        onClose={closeInspect}
        onMove={(nextIndex) => inspect(inspectGroup, nextIndex)}
      />
    </section>
  );
}
